export type LogLevel = 'silent' | 'error' | 'warn' | 'info' | 'debug';

const LEVELS: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

function isLogLevel(value: string): value is LogLevel {
  return value in LEVELS;
}

// Default: In Produktion leiser (warn), in Dev/Preview informativer (info)
const defaultLevel: LogLevel = import.meta.env.PROD ? 'warn' : 'info';
const env: unknown = import.meta.env;
const configuredEnvLevel =
  typeof env === 'object' && env !== null && 'VITE_LOG_LEVEL' in env ? env.VITE_LOG_LEVEL : undefined;
const configuredLevel = typeof configuredEnvLevel === 'string' ? configuredEnvLevel.toLowerCase() : defaultLevel;
const currentLevel: LogLevel = isLogLevel(configuredLevel) ? configuredLevel : defaultLevel;

function shouldLog(level: LogLevel): boolean {
  return LEVELS[level] <= LEVELS[currentLevel];
}

function ts(): string {
  try {
    return new Date().toISOString().split('T')[1]?.replace('Z', '') ?? '';
  } catch {
    return '';
  }
}

export const logger = {
  debug: (...args: unknown[]): void => {
    if (!shouldLog('debug')) return;

    console.log('%c[DEBUG]%c ' + ts(), 'color:#6b7280;font-weight:700', 'color:#9ca3af', ...args);
  },
  info: (...args: unknown[]): void => {
    if (!shouldLog('info')) return;

    console.log('%c[INFO ]%c ' + ts(), 'color:#2563eb;font-weight:700', 'color:#9ca3af', ...args);
  },
  warn: (...args: unknown[]): void => {
    if (!shouldLog('warn')) return;

    console.warn('%c[WARN ]%c ' + ts(), 'color:#f59e0b;font-weight:700', 'color:#9ca3af', ...args);
  },
  error: (...args: unknown[]): void => {
    if (!shouldLog('error')) return;

    console.error('%c[ERROR]%c ' + ts(), 'color:#ef4444;font-weight:700', 'color:#9ca3af', ...args);
  },
};

export default logger;
